/****************************************************
 * Module Name:  UserColumnRole.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { Box, Typography, Tooltip } from "@mui/material";
import Constants from "../../utility/constants";
import DualListBox from "react-dual-listbox";
import "react-dual-listbox/lib/react-dual-listbox.css";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import BlockIcon from "@mui/icons-material/Block";
import AddTaskIcon from "@mui/icons-material/AddTask";
import UserProxy from "../../utility/proxy/manager/userProxy";
import Utility from "../../utility/utility";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import { LoadingButton } from "@mui/lab";
import ColumnRoleProxy from "../../utility/proxy/manager/permissionProxy/columnRoleProxy";
import UserColumnRoleProxy from "../../utility/proxy/manager/permissionProxy/userColumnRoleProxy";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";

const fullOptionToValue = (users, permissions) => {
    let data = {};
    let userIds = [];
    let columnRoleName = [];

    permissions.map((perm) => {
        columnRoleName.push(
            perm.label
        );
        return columnRoleName;
    });

    users.map((option) => {
        userIds.push(option.value);
        return userIds;
    });
    data.userids = userIds;
    data.usercolumnrolename = columnRoleName;
    return data;
}

const UserColumnRole = () => {
    const [user, setUser] = useState("");
    const [userOptions, setUserOptions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [roleOption, setRoleOption] = useState([]);
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [grantLoader, setGrantLoader] = useState(false);
    const [revokeLoader, setRevokeLoader] = useState(false);
    const [userInputValue, setUserInputValue] = useState("");
    const [debouncedInputValue, setDebouncedInputValue] = useState("");
    const [showUserDetailModal, setShowUserDetailModal] = useState(false);
    // eslint-disable-next-line
    const [local_Permission, setLocal_Permission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.PERMISSION_USERROLE_USER
        )
    );

    useEffect(() => {
        getAllUser();
        getAllUserRole();
    }, []);

    const onChange = (value) => {
        setSelected(value);
    };

    const getAllUser = () => {
        UserProxy.GetAllGenericUserMini((response) => {
            if (response) {
                setAllUserData(response)
            } else {
                setErrorSnackbar(true)
            }
        }
        );
    };

    const setAllUserData = (response) => {
        const sortedResponse = Utility.sortFn(response, "username");
        let optionsArray = [];
        sortedResponse.map((data) => {
            let option = { value: data.id, label: data.username };
            optionsArray.push(option);
            return true;
        });
        setUserOptions(optionsArray);
        setUser(user === "" && optionsArray.length > 0 ? [optionsArray[0]] : user);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedInputValue(userInputValue);
        }, 500);

        return () => clearTimeout(handler);
    }, [userInputValue]);

    const filteredUserOptions = useMemo(() => {
        if (!debouncedInputValue) {
            return userOptions.slice(0, 100);
        }
        return userOptions.filter(option =>
            option.label.toLowerCase().includes(debouncedInputValue.toLowerCase())
        ).slice(0, 500);
    }, [debouncedInputValue, userOptions]);

    const getAllUserRole = () => {
        ColumnRoleProxy.GetAllRole((response) => {
            if (response) {
                setAllUserRoleData(response)
            } else {
                setErrorSnackbar(true)
            }
        }
        );
    };

    const setAllUserRoleData = (response) => {
        let optionsArray = [];
        let uniqueId = 0;
        response.map((option) => {
            let option1 = {
                value: ++uniqueId,
                label: option.name,
                sendValue: option.id,
            };
            optionsArray.push(option1);
            return true;
        });
        setRoleOption(optionsArray);
    };

    const handleGrantUserRole = () => {
        let data = fullOptionToValue(user, selected);
        setGrantLoader(true);
        UserColumnRoleProxy.GrantUserColumnRole(data, (response) => {
            if (response) {
                setSnackMessage(response);
                setSuccessSnackbar(true);
                setGrantLoader(false);
            } else {
                setErrorSnackbar(true);
                setGrantLoader(false);
            }
        });
    };

    const handleRevokeUserRole = () => {
        let data = fullOptionToValue(user, selected);
        setRevokeLoader(true);
        UserColumnRoleProxy.RevokeUserColumnRole(data, (response) => {
            if (response) {
                setSnackMessage(response);
                setSuccessSnackbar(true);
                setRevokeLoader(false);
            } else {
                setRevokeLoader(false);
                setErrorSnackbar(true);
            }
        });
    };

    return (
        <Box>
            <TabMenuSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Error"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {successSnackbar ? (
                <TabMenuSnackbar
                    show={true}
                    message={<Box>{snackMessage.errorMessage}</Box>}
                    variant={snackMessage.status.toLowerCase()}
                    autoHide={snackMessage.status === "Success" ? true : false}
                    handleClose={() => setSuccessSnackbar(false)}
                    title={snackMessage.status}
                />
            ) : null}
            {showUserDetailModal ? (
                <UserDetailModal
                    show={true}
                    handleClose={() => setShowUserDetailModal(false)}
                    handleSelect={(selectedOption) => {
                        const selectedCustomers = userOptions.filter(opt =>
                            selectedOption.includes(opt.value)
                        );
                        setUser(selectedCustomers);
                        setShowUserDetailModal(false)
                    }}
                    userids={userOptions.filter(option => option.value !== "*").map(cust => cust.value)}
                    selectedIds={user !== "" ? user.map(iCustomer => iCustomer.value) : []}
                    usertype={"U"}
                    loading={showUserDetailModal}
                    isMulti={true}
                />
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    minWidth: "350px",
                    maxWidth: "auto",
                    gap: "20px",
                    mb: 1,
                    px: 1
                }}
            >
                <Box sx={{ alignSelf: "center", mt: 2 }}>
                    <Typography>Users</Typography>
                </Box>
                <Box
                    sx={{
                        alignSelf: "center",
                        minWidth: "350px",
                        maxWidth: "auto",
                        mt: 2,
                        display: "inline-flex",
                        alignItems: "center",
                    }}
                >
                    <Box sx={{ flexGrow: 1 }}>
                        <Select menuPlacement="auto"
                            blurInputOnSelect={false}
                            menuPortalTarget={document.body}
                            styles={Constants.Styles}
                            isMulti
                            closeMenuOnSelect={false}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            options={filteredUserOptions}
                            value={user}
                            onChange={(e) => setUser(e)}
                            inputValue={userInputValue}
                            onInputChange={(inputValue) => setUserInputValue(inputValue)}
                        />
                    </Box>
                    <Tooltip title="View More Data">
                        <span style={{ cursor: "pointer", marginLeft: "15px", marginTop: "0px" }}>
                            <OpenInNewIcon
                                fontSize="medium"
                                color="action"
                                onClick={() => setShowUserDetailModal(true)}
                            />
                        </span>
                    </Tooltip>
                </Box>
            </Box>
            <Box
                sx={{

                    "& .rdl-controls": {
                        minHeight: "550px",
                        maxHeight: "700px",
                    },
                    backgroundColor: '#fff',
                    color: '#000',
                    height: '55vh',
                    padding: 1
                }}
            >
                <DualListBox
                    canFilter
                    filterPlaceholder={"Search Roles"}
                    // showNoOptionsText
                    showHeaderLabels
                    simpleValue={false}
                    options={roleOption}
                    selected={selected}
                    onChange={onChange}
                    icons={{
                        moveLeft: <KeyboardArrowLeftIcon />,
                        moveAllLeft: <KeyboardDoubleArrowLeftIcon />,
                        moveRight: <KeyboardArrowRightIcon />,
                        moveAllRight: <KeyboardDoubleArrowRightIcon />,
                        // moveDown: <span className="fa fa-chevron-down" />,
                        // moveUp: <span className="fa fa-chevron-up" />,
                        // moveTop: <span className="fa fa-double-angle-up" />,
                        // moveBottom: <span className="fa fa-double-angle-down" />,
                    }}
                />
            </Box>
            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    paddingBottom: 5,
                    paddingRight: 5
                }}
                height={'15vh'}
                display="flex"
                alignItems="end"
                justifyContent="end"
                gap={"15px"}
            >
                {Utility.isUpdateAllowed(local_Permission) ||
                    Utility.isAddAllowed(local_Permission) ? (
                    <LoadingButton
                        variant="contained"
                        startIcon={<AddTaskIcon />}
                        onClick={handleGrantUserRole}
                        loading={grantLoader}
                        loadingPosition="start"
                        disabled={userOptions.length === 0 || selected.length === 0}
                    >
                        Grant
                    </LoadingButton>
                ) : null}
                {Utility.isUpdateAllowed(local_Permission) ||
                    Utility.isDeleteAllowed(local_Permission) ? (
                    <LoadingButton
                        sx={{ bgcolor: "#1a237e" }}
                        variant="contained"
                        loading={revokeLoader}
                        loadingPosition="start"
                        component="label"
                        startIcon={<BlockIcon />}
                        onClick={handleRevokeUserRole}
                        disabled={userOptions.length === 0 || selected.length === 0}
                    >
                        Revoke
                    </LoadingButton>
                ) : null}
            </Box>
        </Box>
    );
};

export default UserColumnRole;
